import { createInput } from '@formkit/vue'
import type {
  FormKitExtendableSchemaRoot,
  FormKitMiddleware,
  FormKitNode,
} from '@formkit/core'
import { createMessage } from '@formkit/core'
import { generateClasses } from '@formkit/themes'
import { clone, empty } from '@formkit/utils'
import {
  FormkitAutocompleteOptions,
  FormkitCalendarOptions,
  FormkitCheckboxGroupOptions,
  FormkitCheckboxOptions,
  FormkitChipsOptions,
  FormkitCodeOptions,
  FormkitColorOptions,
  FormkitIconPickerOptions,
  FormkitDropdownOptions,
  FormkitEditorOptions,
  FormkitInputOptions,
  FormkitMapOptions,
  FormkitMultiSelectOptions,
  FormkitNumberOptions,
  FormkitPasswordOptions,
  FormkitPhoneOptions,
  FormkitPickListOptions,
  FormkitRadioOptions,
  FormkitRecaptchaOptions,
  FormkitSelectButtonOptions,
  FormkitSwitchOptions,
  FormkitTextAreaOptions,
  FormkitToggleButtonOptions,
  FormkitUploaderOptions,
  FormkitUrlOptions,
  FormkitLookupOptions,
} from './options'
import { get } from 'lodash-es'
import FormkitHelp from './components/FormKitHelp.vue'

const FormkitAutocomplete = defineAsyncComponent(() => import('./inputs/FormkitAutocomplete.vue'))
const FormkitChips = defineAsyncComponent(() => import('./inputs/FormkitChips.vue'))
const FormkitEditor = defineAsyncComponent(() => import('./inputs/FormkitEditor.vue'))
const FormkitSwitch = defineAsyncComponent(() => import('./inputs/FormkitSwitch.vue'))
const FormkitLookup = defineAsyncComponent(() => import('./inputs/FormkitLookup.vue'))
const FormkitPassword = defineAsyncComponent(() => import('./inputs/FormkitPassword.vue'))
const FormkitCalendar = defineAsyncComponent(() => import('./inputs/FormkitCalendar.vue'))
const FormkitDropdown = defineAsyncComponent(() => import('./inputs/FormkitDropdown.vue'))
const FormkitTextarea = defineAsyncComponent(() => import('./inputs/FormkitTextarea.vue'))
const FormkitInputText = defineAsyncComponent(() => import('./inputs/FormkitInputText.vue'))
const FormkitColorPicker = defineAsyncComponent(() => import('./inputs/FormkitColorPicker.vue'))
const FormkitIconPicker = defineAsyncComponent(() => import('./inputs/FormkitIconPicker.vue'))
const FormkitInputNumber = defineAsyncComponent(() => import('./inputs/FormkitInputNumber.vue'))
const FormkitMultiSelect = defineAsyncComponent(() => import('./inputs/FormkitMultiSelect.vue'))
const FormkitToggleButton = defineAsyncComponent(() => import('./inputs/FormkitToggleButton.vue'))
const FormkitSelectButton = defineAsyncComponent(() => import('./inputs/FormkitSelectButton.vue'))
const FormkitCheckboxGroup = defineAsyncComponent(() => import('./inputs/FormkitCheckboxGroup.vue'))
const FormkitCheckboxGroupExtended = defineAsyncComponent(
  () => import('./inputs/FormkitCheckboxGroupExtended.vue')
)
const FormkitCheckbox = defineAsyncComponent(() => import('./inputs/FormkitCheckbox.vue'))
const FormkitPickList = defineAsyncComponent(() => import('./inputs/FormkitPickList.vue'))
const FormkitUploader = defineAsyncComponent(() => import('./inputs/FormkitUploader.vue'))
const FormkitPhone = defineAsyncComponent(() => import('./inputs/FormkitPhone.vue'))
const FormkitUrl = defineAsyncComponent(() => import('./inputs/FormkitUrl.vue'))
const FormKitLanguageDependentInput = defineAsyncComponent(
  () => import('./components/FormKitLanguageDependentInput.vue')
)
const FormKitLanguagePreview = defineAsyncComponent(
  () => import('./components/FormKitLanguagePreview.vue')
)
const FormkitRecaptcha = defineAsyncComponent(() => import('./inputs/FormkitRecaptcha.vue'))
const FormkitRadio = defineAsyncComponent(() => import('./inputs/FormkitRadio.vue'))
const FormkitCode = defineAsyncComponent(() => import('./inputs/FormkitCodeMirror.vue'))
const FormkitMap = defineAsyncComponent(() => import('./inputs/FormkitMap.vue'))

export const inputs = {
  autocomplete: createInput(FormkitAutocomplete, FormkitAutocompleteOptions),
  input: createInput(FormkitInputText, FormkitInputOptions),
  numbers: createInput(FormkitInputNumber, FormkitNumberOptions),
  phone: createInput(FormkitPhone, FormkitPhoneOptions),
  textarea: createInput(FormkitTextarea, FormkitTextAreaOptions),
  chips: createInput(FormkitChips, FormkitChipsOptions),
  selectbutton: createInput(FormkitSelectButton, FormkitSelectButtonOptions),
  calendar: createInput(FormkitCalendar, FormkitCalendarOptions),
  switch: createInput(FormkitSwitch, FormkitSwitchOptions),
  checkboxgroup: createInput(FormkitCheckboxGroup, FormkitCheckboxGroupOptions),
  checkboxgroupextended: createInput(FormkitCheckboxGroupExtended, FormkitCheckboxGroupOptions),
  checkbox: createInput(FormkitCheckbox, FormkitCheckboxOptions),
  multiselect: createInput(FormkitMultiSelect, FormkitMultiSelectOptions),
  dropdown: createInput(FormkitDropdown, FormkitDropdownOptions),
  toggle: createInput(FormkitToggleButton, FormkitToggleButtonOptions),
  web: createInput(FormkitUrl, FormkitUrlOptions),
  lookup: createInput(FormkitLookup, FormkitLookupOptions),
  color: createInput(FormkitColorPicker, FormkitColorOptions),
  icon: createInput(FormkitIconPicker, FormkitIconPickerOptions),
  appPassword: createInput(FormkitPassword, FormkitPasswordOptions),
  editor: createInput(FormkitEditor, FormkitEditorOptions),
  uploader: createInput(FormkitUploader, FormkitUploaderOptions),
  pickList: createInput(FormkitPickList, FormkitPickListOptions),
  radio: createInput(FormkitRadio, FormkitRadioOptions),
  code: createInput(FormkitCode, FormkitCodeOptions),
  map: createInput(FormkitMap, FormkitMapOptions),
  recaptcha: createInput(FormkitRecaptcha, FormkitRecaptchaOptions),
}

const addDataTestAttributesPlugin = (node: FormKitNode) => {
  if (node.type === 'input' || node.type === 'list' || node.type === 'group') {
    const formNode = node.at('$root')
    const dataTestId = `formKit-${formNode?.props.id ?? 'genericForm'}${node.parent?.index ? (node.parent.index >= 0 ? `-${node.parent.index}` : node.parent.index) : '-0'}-${node.name}`
    node.props.attrs['data-test-id'] = dataTestId
  }
}

const onUpdateLangageDependentProperties = (
  node: FormKitNode & { path: string },
  payload: object
) => {
  if (payload) {
    const rootNode = node.at('$root')
    const nuxtApp = useNuxtApp()
    nuxtApp.runWithContext(async () => {
      const { previewLocale } = useLocale()
      const queryClient = useQueryClient()
      const dataLocales = await queryClient.fetchQuery(dataLocalesQuery)
      const languageDependentProperties = (node.at('$root.languageDependentProperties')?.value ??
        []) as string[]
      if (
        pathCheckRegex(languageDependentProperties, node.path) ||
        node.context?.languageDependent
      ) {
        const value = rootNode?.context?.attrs['preview-data-serializer']
          ? rootNode?.context?.attrs['preview-data-serializer'](payload, node.path)
          : get(payload, node.path)
        node.store.set(
          createMessage({
            key: 'preview-lang-data',
            blocking: false,
            type: 'info',
            meta: {
              langSymbol: dataLocales?.[previewLocale.value!]?.symbol,
            },
            value: value,
          })
        )
      }
    })
  } else {
    node.store.remove('preview-lang-data')
  }
}

const getLangPreviewDataPlugin = (node: FormKitNode & { path: string }) => {
  const languageDependentPropertiesInput = node.at('$root.languageDependentProperties')
  const languageFallbackInput = node.at('$root.languageFallbackData')

  // Pokud se dříve načtou data z FORMU
  languageDependentPropertiesInput?.on('updateLangValue', () => {
    if (languageFallbackInput?.value) {
      onUpdateLangageDependentProperties(node, languageFallbackInput?.value)
    }
  })
  // Pokud se dříve načtou fallback data
  languageFallbackInput?.on('updateLangValue', ({ payload }) => {
    onUpdateLangageDependentProperties(node, payload)
  })
}

function addAsteriskAndHelpPlugin(node: FormKitNode) {
  const legends = ['checkbox_multi', 'radio_multi', 'repeater', 'transferlist']
  if (
    ['button', 'submit', 'hidden', 'group', 'list', 'meta', 'multi-step'].includes(node.props.type)
  )
    return
  node.on('created', () => {
    const legendOrLabel = legends.includes(
      `${node.props.type}${node.props.options ? '_multi' : ''}`
    )
      ? 'legend'
      : 'label'
    if (node.props.definition) {
      node.props.definition.schemaMemoKey = `required_${node.props.definition.schemaMemoKey}`
    }
    if (node.context) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      node.context.isRequired = node.props.parsedRules.some((rule: any) => rule.name === 'required')
    }

    node.on('prop:validation', () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (node.context && !node.props.parsedRules.some((rule: any) => rule.name === 'require_if')) {
        node.context.isRequired = node.props.parsedRules.some(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (rule: any) => {
            return rule.name === 'required'
          }
        )
      }
    })

    const schemaFn = node.props?.definition?.schema as FormKitExtendableSchemaRoot
    if (node.props.definition) {
      node.props.definition.library = markRaw({
        ...node.props.definition.library,
        FormkitHelp,
        FormKitLanguagePreview,
        FormKitLanguageDependentInput,
      })
      node.props.definition.schema = (sectionsSchema = {}) => {
        if (node.props.type === 'switch' || node.props.type === 'checkbox') {
          sectionsSchema.wrapper = {
            attrs: {
              class: '$classes.wrapper + " formkit-flex " + $sample',
            },
          }
        }
        const getParentPath = (node: FormKitNode): string => {
          let currentNode = node
          let path = node.name + ''
          while (currentNode.parent) {
            if (currentNode.parent.type === 'form') {
              currentNode = currentNode.parent
              continue
            }
            path = currentNode.parent.name + '.' + path
            currentNode = currentNode.parent
          }
          const pathArray = path.split('.')

          return pathArray.slice(1).join('.')
        }
        node.path = getParentPath(node)
        const isRoot = node.props?.attrs?.['is-root'] || node.props?.attrs?.isRoot
        node.context.isRoot = isRoot ?? false
        sectionsSchema.message = {
          children: [
            {
              props: {
                node: '$node',
                message: '$message',
              },
              $cmp: 'FormKitLanguagePreview',
            },
          ],
          attrs: {
            class: '$classes.message',
          },
        }
        sectionsSchema[legendOrLabel] = {
          attrs: {
            class:
              node.context?.type === 'switch'
                ? '$classes.label + " mt-2 w-full cursor-pointer flex"'
                : '$classes.label + " w-full flex"',
          },
          children: [
            '$label',
            {
              $el: 'span',
              if: '$isRequired',
              attrs: {
                class: '$classes.asterisk',
              },
              children: ['*'],
            },
            {
              $el: 'span',
              if: '$isRoot',
              attrs: {
                class: 'fa fa-lock ml-auto text-primary',
              },
            },
            {
              $cmp: 'FormkitHelp',
              if: '$help',
              props: {
                help: '$help',
              },
            },
            {
              $cmp: 'FormKitLanguageDependentInput',
              props: {
                languageDependentInputs: "$node.at('$root.languageDependentProperties').value",
                languageDependent: '$node.context.languageDependent',
                path: '$node.path',
              },
            },
          ],
        }
        return schemaFn(sectionsSchema)
      }
    }
  })
}

function addTabNameToInput(node: FormKitNode) {
  if (['button', 'submit', 'hidden', 'group', 'list', 'meta'].includes(node.props.type)) return
  const instance = getCurrentInstance()
  const { findParentTabNames } = useFormKit()
  node.on('created', () => {
    const { tabNames, sidebarName, accordionName } = findParentTabNames(instance)
    if (node.context) {
      node.context.tabNames = tabNames
      node.context.accordionName = accordionName
      node.context.sidebarName = sidebarName
    }
  })
}

const require_if = function (
  node: FormKitNode,
  rules: { path: string; value: string | boolean | number; reverse: boolean }[]
) {
  let isRequired = false
  rules.forEach(({ path, reverse, value }) => {
    const n = node.at(path) as FormKitNode

    const isAllRulesCompleted = () => {
      if (typeof n?.value === 'boolean') {
        return reverse ? !n.value : n.value
      }
      if (value) {
        return reverse ? n.value === value : n.value !== value
      }
      return empty(n?.value)
    }
    const state = isAllRulesCompleted()
    if (!isRequired && !state) {
      isRequired = true
    }
  })
  if (node.context) {
    node.context.isRequired = isRequired
  }
  if (!empty(node.value)) return true

  if (isRequired && empty(node.value)) {
    return false
  }
}
require_if.skipEmpty = false

export const rules = {
  require_if,
}

export const messages = {
  en: {
    validation: {
      require_if({ name }) {
        return `${name} is required.`
      },
    },
  },
  cs: {
    validation: {
      require_if({ name }) {
        return `${name} je povinné.`
      },
    },
  },
}

// Obalí onSubmit každého formuláře: před předáním dat handleru async vyřeší pending media
// (url/base64) přes hooky — AppDocumentsUpload instance je nahrají a přepíšou na id, FormkitContent
// přeserializuje strom. Submit hook je v FormKitu sync, proto obalujeme onSubmit (jediný awaitovaný
// krok). Nastavení onSubmit prochází přes hook.prop, takže chytneme i re-set (inline @submit handler
// mění identitu při každém renderu).
// Exportovaný samostatně kvůli projektům s vlastním `formkit.configFile` — ten plně nahrazuje
// skeletí config včetně `plugins`, takže si tenhle plugin musí přidat do svého pole samy.
const PENDING_MEDIA_WRAPPED = Symbol.for('pendingMediaWrapped')
export function resolvePendingMediaPlugin(node: FormKitNode) {
  if (node.props.type !== 'form') return
  const nuxtApp = useNuxtApp()

  const wrap = (orig: unknown): unknown => {
    if (
      typeof orig !== 'function' ||
      (orig as unknown as Record<symbol, unknown>)[PENDING_MEDIA_WRAPPED]
    ) {
      return orig
    }
    const wrapped = async (_data: unknown, formNode: FormKitNode) => {
      try {
        await nuxtApp.callHook('documents:resolvePending', { node: formNode })
        await nuxtApp.callHook('documents:pendingResolved', { node: formNode })
      } catch {
        // upload selhal (toast zobrazil uploader) → přerušit save, formulář zůstane použitelný
        return
      }
      // znovu načíst čerstvou hodnotu (po resolve má structureContent / pole už id)
      return (orig as (d: unknown, n: FormKitNode) => unknown)(
        clone(formNode.value as Record<string, unknown>),
        formNode
      )
    }
    ;(wrapped as unknown as Record<symbol, unknown>)[PENDING_MEDIA_WRAPPED] = true
    return wrapped
  }

  node.props.onSubmit = wrap(node.props.onSubmit)
  const onSubmitPropHook: FormKitMiddleware<{ prop: string | symbol; value: unknown }> = (
    payload,
    next
  ) => next(payload.prop === 'onSubmit' ? { ...payload, value: wrap(payload.value) } : payload)
  node.hook.prop(onSubmitPropHook)
}

export const plugins = [
  addAsteriskAndHelpPlugin,
  addTabNameToInput,
  addDataTestAttributesPlugin,
  getLangPreviewDataPlugin,
  resolvePendingMediaPlugin,
]

export const config = {
  classes: generateClasses({
    global: {
      messages: 'list-none m-0 p-0 mb-2',
      message: 'p-error',
      outer: 'field',
    },
    'family:button': {
      input: 'p-button',
    },
  }),
}
